import React, { useState, useRef, useEffect } from "react";
import dayjs from "dayjs";
import {
  Box,
  TextField,
  Popper,
  Paper,
  Button,
  Typography,
  ClickAwayListener,
  IconButton,
  Divider,
  Tooltip,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDownRounded";
import ClearIcon from "@mui/icons-material/ClearRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import { useTheme } from "@mui/material";
import { getGridButtonSelectorStyles, getSaveButtonStyles } from "../theme";
import { SERVICE_QUICK_RANGES } from "helpers/serviceQuickDateRange";

const CustomDateSelector = ({
  label = "Date Range",
  months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ],
  years = [],
  selectedMonths = [],
  selectedYears = [],
  onChange,
  onSave,
  displaySuffix = "",
  monthGridItemSize = 3,
  yearGridItemSize = 4,
  isMultiMonth = true,
  isMultiYear = true,
  showClearFilter = true,
  iconOnly = false,
  useInnerShadow = false,
  useSimpleStyle = false,
  enabledYears = null,
  enabledMonthsByYear = null,
  // 2026-11-03 (Amine): quick range presets, rendered at the top of the popover
  // just above Months. Opt-in: pass `onQuickRangeChange` to enable them, which
  // only the three service lists do. The presets cannot be expressed as
  // month/year alone (Today / Tomorrow / This week are day-level), so this
  // component only REPORTS the chosen range — the screen applies the day
  // predicate and widens the fetch through helpers/serviceQuickDateRange.
  quickRangeValue = null,
  onQuickRangeChange = null,
  // 2026-11-03 (Amine): clicking ANY month or year clears the chosen quick
  // range — the two describe the same thing, so the range must not survive a
  // manual month choice.
  //
  // Deliberately a PLAIN setter, not `onQuickRangeChange`: that one moves the
  // month and refetches, which would fight the click the user just made. This
  // one only stores the value.
  onQuickRangeSet = null,
  ...otherProps
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [localSelectedMonths, setLocalSelectedMonths] = useState(
    selectedMonths || [],
  );
  const [localSelectedYears, setLocalSelectedYears] = useState(
    selectedYears || [],
  );
  const textFieldRef = useRef(null);
  // 2026-11-03 (Amine): the range this popover opened with, and whether a
  // month/year click has already cleared it during this visit.
  const quickRangeAtOpenRef = useRef(quickRangeValue);
  const quickRangeClearedRef = useRef(false);

  // Update local state when props change
  // 2026-11-03 (Amine): keys, NOT array identities.
  //
  // Callers build `months` as an inline literal (`const months = ["Jan", …]`)
  // inside the component body, so it is a NEW array on every render. Depending
  // on it directly meant this effect re-ran after every render and overwrote
  // `localSelectedMonths` with the saved prop — which silently undid the first
  // month click (the click set local state, then the next render reset it from
  // the prop). The second click worked because by then nothing else changed,
  // so no extra render happened. Comparing the joined contents keeps the reset
  // for a genuine prop change and ignores a merely rebuilt array.
  const monthsKey = (months || []).join("|");
  const selectedMonthsKey = (selectedMonths || []).join("|");
  const selectedYearsKey = (selectedYears || []).map(String).join("|");

  useEffect(() => {
    // For months, sort based on their order in the months array
    const sortedMonths = [...(selectedMonths || [])].sort((a, b) => {
      return months.indexOf(a) - months.indexOf(b);
    });
    setLocalSelectedMonths(sortedMonths);

    // For years, preserve the original order from the selectedYears prop
    setLocalSelectedYears(selectedYears || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonthsKey, selectedYearsKey, monthsKey]);

  const handleClick = () => {
    if (anchorEl) {
      // Closing via the field is an abandon, same as clicking away.
      handleClose();
      return;
    }
    // Remember the range this popover opened with, so an abandoned visit can
    // put it back instead of silently widening the user's list.
    quickRangeAtOpenRef.current = quickRangeValue;
    quickRangeClearedRef.current = false;
    setAnchorEl(textFieldRef.current);
  };

  const handleClose = () => {
    // Closed without saving: undo the range clear caused by the month clicks.
    if (quickRangeClearedRef.current && onQuickRangeSet) {
      onQuickRangeSet(quickRangeAtOpenRef.current);
    }
    quickRangeClearedRef.current = false;
    setAnchorEl(null);
  };

  // Called by every month/year interaction. Idempotent: the first click in a
  // visit wins, so the snapshot stays the range from before the interaction.
  //
  // Clears to NULL, not to the "This month" default. The screens highlight
  // "This month" whenever they are ON the current month, so clearing to that
  // value lit the button straight back up as soon as the user clicked the
  // current month or its year. NULL means "nothing selected", which is what a
  // manual month choice has to produce.
  const clearQuickRange = () => {
    if (!onQuickRangeSet || quickRangeClearedRef.current) return;
    quickRangeClearedRef.current = true;
    onQuickRangeSet(null);
  };

  const handleSave = () => {
    // For months, sort based on their order in the months array
    const sortedMonths = [...localSelectedMonths].sort((a, b) => {
      return months.indexOf(a) - months.indexOf(b);
    });

    // For years, preserve the original selection order
    const selectedYearsList = [...localSelectedYears];

    // Call the onSave callback with the selected items
    if (onSave) {
      onSave({ months: sortedMonths, years: selectedYearsList });
    } else if (onChange) {
      // Fallback to onChange if onSave is not provided
      onChange({ months: sortedMonths, years: selectedYearsList });
    }
    // Saving commits the month choice, so the range clear must NOT be undone.
    quickRangeClearedRef.current = false;
    handleClose();
  };

  const handleSelectAllMonths = () => {
    clearQuickRange();
    // Use the original order of months when selecting all.
    // If availability is provided, only select months that are currently enabled.
    if (enabledMonthsByYear && typeof enabledMonthsByYear === "object") {
      const yearsToCheck =
        localSelectedYears.length > 0 ? localSelectedYears : years;
      const availableMonthSet = new Set();

      yearsToCheck.forEach((year) => {
        const list = enabledMonthsByYear[String(year)] || [];
        list.forEach((month) => availableMonthSet.add(month));
      });

      const selectableMonths = months.filter((month) =>
        availableMonthSet.has(month),
      );
      setLocalSelectedMonths(selectableMonths);
      return;
    }

    setLocalSelectedMonths([...months]);
  };

  const handleSelectNoneMonths = () => {
    clearQuickRange();
    setLocalSelectedMonths([]);
  };

  const handleMonthToggle = (month) => {
    if (enabledMonthsByYear && typeof enabledMonthsByYear === "object") {
      const yearsToCheck =
        localSelectedYears.length > 0 ? localSelectedYears : years;
      const isMonthEnabled = yearsToCheck.some((year) =>
        (enabledMonthsByYear[String(year)] || []).includes(month),
      );

      if (!isMonthEnabled) {
        return;
      }
    }

    clearQuickRange();

    if (isMultiMonth) {
      // Multi-select mode
      setLocalSelectedMonths((prev) => {
        let newSelection;
        if (prev.includes(month)) {
          newSelection = prev.filter((i) => i !== month);
        } else {
          newSelection = [...prev, month];
        }

        // Sort the selection based on the order in the months array
        return newSelection.sort((a, b) => {
          return months.indexOf(a) - months.indexOf(b);
        });
      });
    } else {
      // Single-select mode - replace the entire selection
      setLocalSelectedMonths([month]);

      // If we're in single select mode and there's no onSave callback,
      // we can automatically apply the selection but keep the popup open
      if (!onSave && !isMultiYear) {
        onChange && onChange({ months: [month], years: localSelectedYears });
      }
    }
  };

  const handleYearToggle = (year) => {
    if (Array.isArray(enabledYears) && enabledYears.length > 0) {
      if (!enabledYears.map(String).includes(String(year))) {
        return;
      }
    }

    clearQuickRange();

    const currentYear = dayjs().year();
    const hasMonthsAvailable = months && months.length > 0;
    const currentMonth = hasMonthsAvailable ? months[dayjs().month()] : null;
    const yearAvailableMonths =
      enabledMonthsByYear && typeof enabledMonthsByYear === "object"
        ? enabledMonthsByYear[String(year)] || []
        : months;

    // If it's the current year, clear all months then select current month
    // If it's not the current year, clear all months
    // Only do this if we have months available
    if (hasMonthsAvailable) {
      if (
        Number(year) === currentYear &&
        currentMonth &&
        yearAvailableMonths.includes(currentMonth)
      ) {
        setLocalSelectedMonths([currentMonth]);
      } else if (yearAvailableMonths.length > 0) {
        setLocalSelectedMonths([yearAvailableMonths[0]]);
      } else {
        setLocalSelectedMonths([]);
      }
    }

    if (isMultiYear) {
      // Multi-select mode
      setLocalSelectedYears((prev) => {
        let newSelection;
        if (prev.includes(year)) {
          newSelection = prev.filter((i) => i !== year);
        } else {
          newSelection = [...prev, year];
        }

        // Preserve the original order from the years prop
        // by sorting based on the index in the years array
        return newSelection.sort((a, b) => {
          return years.indexOf(a) - years.indexOf(b);
        });
      });
    } else {
      // Single-select mode - replace the entire selection
      setLocalSelectedYears([year]);

      // If we're in single select mode and there's no onSave callback,
      // we can automatically apply the selection but keep the popup open
      if (!onSave && !isMultiMonth) {
        onChange &&
          onChange({
            months: year === currentYear && currentMonth ? [currentMonth] : [],
            years: [year],
          });
      }
    }
  };

  const getDisplayText = () => {
    if (localSelectedMonths.length === 0 && localSelectedYears.length === 0) {
      return `Select ${label.toLowerCase()}`;
    }

    let monthText = "";
    const hasMonths = months && months.length > 0;

    if (hasMonths) {
      if (localSelectedMonths.length === 0) {
        monthText = "No months";
      } else if (localSelectedMonths.length === 1) {
        monthText = localSelectedMonths[0];
      } else if (localSelectedMonths.length === months.length) {
        monthText = "All months";
      } else if (localSelectedMonths.length <= 3) {
        monthText = localSelectedMonths.join(", ");
      } else {
        monthText = `${localSelectedMonths.length} months`;
      }
    }

    let yearText = "";
    if (localSelectedYears.length === 0) {
      yearText = "No years";
    } else if (localSelectedYears.length === 1) {
      yearText = localSelectedYears[0].toString();
    } else if (localSelectedYears.length === years.length) {
      yearText = "All years";
    } else if (localSelectedYears.length <= 2) {
      yearText = localSelectedYears.join(", ");
    } else {
      yearText = `${localSelectedYears.length} years`;
    }

    // Combine the text parts
    if (hasMonths && monthText) {
      return `${monthText} / ${yearText}${displaySuffix}`;
    } else {
      return `${yearText}${displaySuffix}`;
    }
  };

  const open = Boolean(anchorEl);
  const hasMonths = months && months.length > 0;
  const isSaveDisabled = hasMonths
    ? localSelectedMonths.length === 0
    : localSelectedYears.length === 0;
  const hasActiveFilter =
    localSelectedMonths.length > 0 || localSelectedYears.length > 0;

  const selectorShadow = useSimpleStyle
    ? "none"
    : useInnerShadow
      ? "inset 0 2px 6px rgba(0, 0, 0, 0.14), inset 0 -1px 3px rgba(255, 255, 255, 0.35)"
      : "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)";
  const selectorHoverShadow = useSimpleStyle
    ? "none"
    : useInnerShadow
      ? "inset 0 3px 8px rgba(0, 0, 0, 0.18), inset 0 -1px 4px rgba(255, 255, 255, 0.4)"
      : "0 4px 12px rgba(0, 0, 0, 0.15)";
  const selectorBorder = useSimpleStyle
    ? `1px solid ${theme.palette.divider}`
    : "none";
  const selectorHoverBorder = useSimpleStyle
    ? `1px solid ${theme.palette.divider}`
    : "none";
  const selectorBackgroundColor = useSimpleStyle
    ? theme.palette.background.default
    : useInnerShadow
      ? theme.palette.background.alt
      : theme.palette.background.default;

  const handleClearFilter = () => {
    setLocalSelectedMonths([]);
    setLocalSelectedYears([]);
    if (onSave) {
      onSave({ months: [], years: [] });
    } else if (onChange) {
      onChange({ months: [], years: [] });
    }
    handleClose();
  };

  return (
    <Box
      sx={{ position: "relative", width: iconOnly ? "auto" : "100%" }}
      {...otherProps}
    >
      {iconOnly ? (
        <Tooltip title={label}>
          <IconButton
            ref={textFieldRef}
            onClick={handleClick}
            size="small"
            sx={{
              height: "40px",
              width: "40px",
              backgroundColor: selectorBackgroundColor,
              boxShadow: selectorShadow,
              border: selectorBorder,
              borderRadius: "12px",
              color: theme.palette.text.primary,
              "&:hover": {
                backgroundColor: selectorBackgroundColor,
                boxShadow: selectorHoverShadow,
                border: selectorHoverBorder,
              },
            }}
          >
            <CalendarMonthRoundedIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <TextField
          ref={textFieldRef}
          fullWidth
          size="small"
          label={label}
          //variant="filled"
          value={getDisplayText()}
          onClick={handleClick}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <IconButton
                edge="end"
                onClick={handleClick}
                sx={{ padding: 0.5 }}
              >
                <ArrowDropDownIcon />
              </IconButton>
            ),
          }}
          sx={{
            height: "100%",
            "& .MuiInputBase-root": {
              height: "100%",
              display: "flex",
              alignItems: "center",
              backgroundColor: selectorBackgroundColor,
              boxShadow: selectorShadow,
              border: selectorBorder,
              borderRadius: "12px",
              "&:hover": {
                backgroundColor: selectorBackgroundColor,
                boxShadow: selectorHoverShadow,
                border: selectorHoverBorder,
              },
            },
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none !important",
            },
            "& .MuiInputBase-input": {
              py: "8px !important",
            },
          }}
        />
      )}

      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        sx={{
          zIndex: 1300,
          width: { xs: "calc(100vw - 24px)", sm: 320 },
          maxWidth: "calc(100vw - 24px)",
        }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper
            elevation={8}
            sx={{
              p: 2,
              bgcolor: theme.palette.background.alt,
              border: `1px solid ${theme.palette.background.default}`,
              borderRadius: "12px",
            }}
          >
            {/* Clear Filter Button */}
            {showClearFilter && hasActiveFilter && (
              <>
                <Button
                  onClick={handleClearFilter}
                  size="small"
                  variant="outlined"
                  color="primary"
                  startIcon={<ClearIcon />}
                  fullWidth
                  sx={{ mb: 1.5 }}
                >
                  Clear Filter
                </Button>
                <Divider sx={{ mb: 1.5 }} />
              </>
            )}

            {/* 2026-11-03 (Amine): quick ranges. Sits ABOVE Months because it is
                the shortcut — pick a window and the screen re-scopes the fetch
                and narrows the rows; the month grid below reflects whatever
                months that range covers.
                Styled with the SAME building blocks as Months/Years
                (getGridButtonSelectorStyles + body2 label) so it reads as part
                of the overlay rather than a bolted-on toolbar. */}
            {onQuickRangeChange && (
              <>
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      mb: 1,
                      fontWeight: "bold",
                      color: theme.palette.primary.text2,
                    }}
                  >
                    Quick range
                  </Typography>

                  <Grid2 container spacing={1}>
                    {SERVICE_QUICK_RANGES.map((option) => {
                      const isSelected = quickRangeValue === option.value;
                      return (
                        <Grid2 size={{ xs: 4 }} key={option.value}>
                          <Box
                            sx={{
                              ...getGridButtonSelectorStyles(theme, isSelected),
                              // 2026-11-03 (Amine): the boxes were different
                              // heights because "This month" / "Next week"
                              // wrapped onto a second line while the others fit
                              // on one. A fixed height plus a no-wrap, slightly
                              // smaller label keeps all five identical.
                              height: 44,
                              px: 0.5,
                            }}
                            onClick={() => onQuickRangeChange(option.value)}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: isSelected
                                  ? "#ffffff"
                                  : theme.palette.primary.main,
                                fontWeight: isSelected ? "bold" : "normal",
                                fontSize: "0.75rem",
                                lineHeight: 1.1,
                                whiteSpace: "nowrap",
                                textAlign: "center",
                              }}
                            >
                              {option.label}
                            </Typography>
                          </Box>
                        </Grid2>
                      );
                    })}
                  </Grid2>
                </Box>

                <Divider
                  sx={{
                    my: 2,
                    borderColor: theme.palette.divider,
                  }}
                />
              </>
            )}

            {/* Months Section - Only show if months exist */}
            {hasMonths && (
              <>
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      mb: 1,
                      fontWeight: "bold",
                      color: theme.palette.primary.text2,
                    }}
                  >
                    Months
                  </Typography>

                  {/* Only show Select All/None buttons in multi-select mode */}
                  {isMultiMonth && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Button
                        onClick={handleSelectAllMonths}
                        size="small"
                        sx={{ color: theme.palette.primary.main }}
                      >
                        Select All
                      </Button>
                      <Button
                        onClick={handleSelectNoneMonths}
                        size="small"
                        sx={{ color: theme.palette.primary.main }}
                      >
                        Select None
                      </Button>
                    </Box>
                  )}

                  <Grid2 container spacing={1}>
                    {months.map((month) => {
                      const isSelected = localSelectedMonths.includes(month);
                      const yearsToCheck =
                        localSelectedYears.length > 0 ? localSelectedYears : years;
                      const isEnabled =
                        !enabledMonthsByYear ||
                        typeof enabledMonthsByYear !== "object"
                          ? true
                          : yearsToCheck.some((year) =>
                              (enabledMonthsByYear[String(year)] || []).includes(
                                month,
                              ),
                            );

                      return (
                        <Grid2 size={{ xs: monthGridItemSize }} key={month}>
                          <Box
                            sx={
                              isEnabled
                                ? getGridButtonSelectorStyles(theme, isSelected)
                                : {
                                    ...getGridButtonSelectorStyles(theme, false),
                                    cursor: "not-allowed",
                                    opacity: 0.45,
                                    boxShadow: "none",
                                    backgroundColor: theme.palette.action.disabledBackground,
                                    borderColor: theme.palette.action.disabled,
                                  }
                            }
                            onClick={() => {
                              if (isEnabled) {
                                handleMonthToggle(month);
                              }
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                    color: isEnabled
                                      ? isSelected
                                        ? "#ffffff"
                                        : theme.palette.primary.main
                                      : theme.palette.text.disabled,
                                fontWeight: isSelected ? "bold" : "normal",
                              }}
                            >
                              {month}
                            </Typography>
                          </Box>
                        </Grid2>
                      );
                    })}
                  </Grid2>
                </Box>

                {/* Divider */}
                <Divider
                  sx={{
                    my: 2,
                    borderColor: theme.palette.divider,
                  }}
                />
              </>
            )}

            {/* Years Section */}
            <Box>
              <Typography
                variant="subtitle1"
                sx={{
                  mb: 1,
                  fontWeight: "bold",
                  color: theme.palette.primary.text2,
                }}
              >
                Years
              </Typography>

              <Grid2 container spacing={1}>
                {years.map((year) => {
                  const isSelected = localSelectedYears.includes(year);
                  const isEnabled =
                    !Array.isArray(enabledYears) ||
                    enabledYears.length === 0 ||
                    enabledYears.map(String).includes(String(year));

                  return (
                    <Grid2 size={{ xs: yearGridItemSize }} key={year}>
                      <Box
                        sx={
                          isEnabled
                            ? getGridButtonSelectorStyles(theme, isSelected)
                            : {
                                ...getGridButtonSelectorStyles(theme, false),
                                cursor: "not-allowed",
                                opacity: 0.45,
                                boxShadow: "none",
                                backgroundColor: theme.palette.action.disabledBackground,
                                borderColor: theme.palette.action.disabled,
                              }
                        }
                        onClick={() => {
                          if (isEnabled) {
                            handleYearToggle(year);
                          }
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: isEnabled
                              ? isSelected
                                ? "#ffffff"
                                : theme.palette.primary.main
                              : theme.palette.text.disabled,
                            fontWeight: isSelected ? "bold" : "normal",
                          }}
                        >
                          {year}
                        </Typography>
                      </Box>
                    </Grid2>
                  );
                })}
              </Grid2>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={isSaveDisabled}
                sx={getSaveButtonStyles(theme, isSaveDisabled)}
              >
                Save
              </Button>
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};

export default CustomDateSelector;
